/**
 * Color mode preference — consumer app theme (light / dark / system).
 * Persisted in localStorage + cookie so SSR matches the client (hydration-safe).
 * Applied via html[data-theme].
 */

export const COLOR_MODE_STORAGE_KEY = "sio-color-mode";
/** Same value as storage key — readable on the server via `cookies()`. */
export const COLOR_MODE_COOKIE_NAME = COLOR_MODE_STORAGE_KEY;
export const COLOR_MODE_CHANGE_EVENT = "sio-color-mode-change";

/** 1 year — preference is sticky across sessions. */
const COLOR_MODE_COOKIE_MAX_AGE_SEC = 60 * 60 * 24 * 365;

export type ColorModePreference = "system" | "light" | "dark";
export type ResolvedColorMode = "light" | "dark";

export const COLOR_MODE_PREFERENCES: ColorModePreference[] = [
  "system",
  "light",
  "dark",
];

export const THEME_META_COLORS: Record<ResolvedColorMode, string> = {
  light: "#4CAF50",
  dark: "#0c0a09",
};

export function isColorModePreference(
  value: string | null | undefined,
): value is ColorModePreference {
  return value === "system" || value === "light" || value === "dark";
}

export function parseColorModePreference(
  value: string | null | undefined,
): ColorModePreference {
  return isColorModePreference(value) ? value : "system";
}

export function readStoredColorModePreference(): ColorModePreference {
  if (typeof window === "undefined") {
    return "system";
  }
  try {
    const stored = localStorage.getItem(COLOR_MODE_STORAGE_KEY);
    if (isColorModePreference(stored)) {
      return stored;
    }
  } catch {
    // localStorage blocked — fall back to system
  }
  return "system";
}

export function resolveColorMode(
  preference: ColorModePreference,
  prefersDark: boolean,
): ResolvedColorMode {
  if (preference === "light") return "light";
  if (preference === "dark") return "dark";
  return prefersDark ? "dark" : "light";
}

export function getSystemPrefersDark(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

/**
 * SSR snapshot for useSyncExternalStore — must equal the cookie-backed preference
 * passed into ThemeProvider from the root layout.
 */
export function getServerColorModePreference(
  initialPreference: ColorModePreference = "system",
): ColorModePreference {
  return parseColorModePreference(initialPreference);
}

export function getClientColorModePreference(): ColorModePreference {
  return readStoredColorModePreference();
}

export function subscribeColorModePreference(
  onStoreChange: () => void,
): () => void {
  const handler = () => onStoreChange();
  window.addEventListener("storage", handler);
  window.addEventListener(COLOR_MODE_CHANGE_EVENT, handler);
  return () => {
    window.removeEventListener("storage", handler);
    window.removeEventListener(COLOR_MODE_CHANGE_EVENT, handler);
  };
}

/** Stable SSR / hydration snapshot for prefers-color-scheme. */
export function getServerSystemPrefersDark(): boolean {
  return false;
}

export function getClientSystemPrefersDark(): boolean {
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function subscribeSystemPrefersDark(
  onStoreChange: () => void,
): () => void {
  const media = window.matchMedia("(prefers-color-scheme: dark)");
  const handler = () => onStoreChange();
  media.addEventListener("change", handler);
  return () => media.removeEventListener("change", handler);
}

export function applyResolvedColorMode(resolved: ResolvedColorMode): void {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", resolved);
}

function writeColorModeCookie(preference: ColorModePreference): void {
  if (typeof document === "undefined") return;
  const secure =
    typeof window !== "undefined" && window.location.protocol === "https:"
      ? "; Secure"
      : "";
  document.cookie = `${COLOR_MODE_COOKIE_NAME}=${preference}; Path=/; Max-Age=${COLOR_MODE_COOKIE_MAX_AGE_SEC}; SameSite=Lax${secure}`;
}

export function writeStoredColorModePreference(
  preference: ColorModePreference,
): void {
  try {
    localStorage.setItem(COLOR_MODE_STORAGE_KEY, preference);
    writeColorModeCookie(preference);
    window.dispatchEvent(new Event(COLOR_MODE_CHANGE_EVENT));
  } catch {
    // ignore quota / private mode
  }
}

/**
 * Sync cookie from localStorage on first paint so the *next* SSR request matches.
 * Also applies data-theme before React hydrates (avoids light flash).
 */
export const COLOR_MODE_INIT_SCRIPT = `(function(){try{var k=${JSON.stringify(COLOR_MODE_STORAGE_KEY)};var p=localStorage.getItem(k);if(p!=="system"&&p!=="light"&&p!=="dark")p="system";document.cookie=k+"="+p+"; Path=/; Max-Age=${COLOR_MODE_COOKIE_MAX_AGE_SEC}; SameSite=Lax"+(location.protocol==="https:"?"; Secure":"");var r="light";if(p==="dark")r="dark";else if(p!=="light"&&window.matchMedia("(prefers-color-scheme: dark)").matches)r="dark";document.documentElement.setAttribute("data-theme",r);}catch(e){}})();`;
